// Adapter: maps the live Tagdada feed schema -> the internal shape used by the UI.
// The live feed and mock both go through this so the app has a single source of truth.

const LISTING_TYPE_MAP = { short_term: "short-term" };

const toCoord = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const avatarFor = (name) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || "T")}&background=F26B2A&color=fff&bold=true`;

const normImage = (img) => {
  if (!img || !img.url) return null;
  return {
    url: img.url,
    verified: img.verified === true || img.kind === "authenticated",
    isFloorPlan: img.group === "floor_plan",
  };
};

// building.amenities + sportsGrounds (+parent "sports") + pickupPoints (+parent "pickup")
const collectAmenities = (b = {}) => {
  const out = [...(b.amenities || [])];
  const sg = b.sportsGrounds || [];
  if (sg.length) out.push("sports");
  sg.forEach((a) => out.push(a));
  const pp = b.pickupPoints || [];
  if (pp.length) out.push("pickup");
  pp.forEach((a) => out.push(a));
  return Array.from(new Set(out));
};

export const normalizeListing = (raw) => {
  if (!raw || raw.__normalized) return raw;
  const specs = raw.specs || {};
  const b = specs.building || {};
  const counts = specs.counts || {};
  const av = raw.availability || {};
  const c = raw.contact;

  const contact = c
    ? {
        name: c.name || null,
        company: c.company || null,
        phone: c.phone || null,
        telegram: c.messenger || null,
        email: c.email || null,
        www: c.website || null,
        avatar: c.name ? avatarFor(c.name) : null,
      }
    : null;

  return {
    __normalized: true,
    id: raw.id,
    publicUrl: raw.publicUrl,
    listingType: LISTING_TYPE_MAP[raw.listingType] || raw.listingType,
    propertyType: raw.propertyType,
    title: raw.title,
    subtitle: raw.subtitle,
    descriptionHtml: raw.descriptionHtml,
    publishedAt: raw.publishedAt,
    updatedAt: raw.updatedAt,
    buildingName: b.name || null,
    location: {
      country: raw.location?.country,
      city: raw.location?.city,
      address: raw.location?.address,
      timezone: raw.location?.timezone,
      verification: raw.location?.verification?.status ?? null, // matched | mismatch | unavailable
      lat: toCoord(raw.location?.lat),
      lng: toCoord(raw.location?.lng ?? raw.location?.lon),
    },
    price: raw.price || { amount: null, currency: "RUB", period: null },
    metro: raw.metro || null, // not in feed (phase 2 geocoding)
    cover: normImage(raw.cover) || (raw.images?.length ? normImage(raw.images[0]) : null),
    images: (raw.images || []).map(normImage).filter(Boolean),
    specs: {
      area: specs.surfaceM2 ?? null,
      rooms: specs.roomsCount ?? 0,
      bedrooms: counts.bedrooms ?? 0,
      floor: specs.floor ?? null,
      totalFloors: b.floorsTotal ?? null,
      kitchenArea: specs.kitchenSurfaceM2 ?? null,
      livingArea: specs.livingSurfaceM2 ?? null,
      builtYear: b.yearBuilt ?? null,
      orientation: specs.orientation ?? null,
      counts: { bathrooms: counts.bathrooms ?? 0, wcs: counts.wcs ?? 0 },
      wcSeparate: specs.wcSeparate ?? (counts.wcs ?? 0) > 0,
      bathWithTub: specs.bathWithTub ?? (counts.bathrooms ?? 0) > 0,
      parking: specs.parking === true,
    },
    features: {
      furnished: specs.furnished ?? null,
      petsAllowed: specs.petsAllowed ?? null,
      adultsOnly: specs.adultsOnly === true,
      noCommission: specs.noCommission === true,
      cargoLift: b.hasCargoLift ?? specs.cargoLift ?? null,
      terraceArea: specs.terraceSurfaceM2 ?? null,
      parkingOutdoor: specs.parkingOutdoor ?? 0,
      parkingUnderground: specs.parkingUnderground ?? 0,
      byOwner: raw.createdByOwnerAgent === "owner",
      amenities: collectAmenities(b),
    },
    availability: {
      earliestSlot: av.hasSlotToday ? "today" : av.nextAvailableAt || null,
      availableFrom: specs.availableFrom ?? null,
      hasSlotToday: av.hasSlotToday === true,
      slotsNext7Days: av.slotsNext7Days ?? null,
      nextAvailableAt: av.nextAvailableAt ?? null,
    },
    contact,
    gold: raw.gold || null,
    source: raw.source,
  };
};

export const normalizeFeed = (data) => ({
  ...(data || {}),
  items: (data?.items || []).map(normalizeListing),
});
